import React, { useState, useEffect, useMemo } from 'react';
import { useCreateTask, useUpdateTask, useUsers, useProjects } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import Modal from './ui/Modal';
import Button from './ui/Button';
import { FormField, FormGrid, Input, Select, Textarea } from './ui/Form';
import { Alert } from './ui/States';

const FORM_ID = 'task-form';

const TaskModal = ({ task, onClose }) => {
    const { user: currentUser } = useAuth();
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        task_code: '',
        project_id: '',
        assignee_id: '',
        priority: 'medium',
        status: 'todo',
        start_date: '',
        due_date: '',
        estimated_hours: '',
        tags: '',
    });

    const { data: userResult } = useUsers();
    const { data: projectResult } = useProjects({
        member_id: formData.assignee_id || undefined,
        limit: 1000
    });
    const users = userResult?.data || [];
    // Memoised: a fresh [] on every render would re-run the effect below forever.
    const projects = useMemo(() => projectResult?.data || [], [projectResult]);
    const createTask = useCreateTask();
    const updateTask = useUpdateTask();
    const isEditMode = !!task;

    const adminRoles = ['Manager', 'Head of Department', 'Developer'];
    const isGlobalAdmin = adminRoles.includes(currentUser?.role);
    const canEditAllFields = !isEditMode || isGlobalAdmin;

    useEffect(() => {
        if (task) {
            setFormData({
                title: task.title || '',
                description: task.description || '',
                task_code: task.task_code || '',
                project_id: task.project_id || '',
                assignee_id: task.assignee_id || '',
                priority: task.priority || 'medium',
                status: task.status || 'todo',
                start_date: task.start_date ? new Date(task.start_date).toISOString().split('T')[0] : '',
                due_date: task.due_date ? new Date(task.due_date).toISOString().split('T')[0] : '',
                estimated_hours: task.estimated_hours || '',
                tags: task.tags || '',
            });
        }
    }, [task]);

    // Validation to clear project if not in filtered list
    useEffect(() => {
        if (formData.project_id && projectResult && formData.assignee_id) {
            const projectExists = projects.find(p => String(p.id) === String(formData.project_id));
            if (!projectExists) {
                setFormData(prev => ({ ...prev, project_id: '' }));
            }
        }
    }, [projects, projectResult, formData.assignee_id, formData.project_id]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEditMode) {
                await updateTask.mutateAsync({ id: task.id, data: formData });
            } else {
                await createTask.mutateAsync(formData);
            }
            onClose();
        } catch (error) {
            console.error('Error saving task:', error);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const isLoading = createTask.isPending || updateTask.isPending;

    return (
        <Modal
            isOpen
            onClose={onClose}
            size="lg"
            title={isEditMode ? 'Edit task' : 'New task'}
            subtitle={
                isEditMode
                    ? 'Update the details of this work item.'
                    : 'Describe the work, assign an owner and set a due date.'
            }
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button type="submit" form={FORM_ID} variant="primary" loading={isLoading}>
                        {isEditMode ? 'Save changes' : 'Create task'}
                    </Button>
                </>
            }
        >
            {!canEditAllFields && (
                <Alert variant="info" className="mb-4">
                    Your role lets you update the status of this task. The remaining fields are managed
                    by a project manager.
                </Alert>
            )}

            <form id={FORM_ID} onSubmit={handleSubmit} className="space-y-4">
                <FormField label="Task title" required htmlFor="task-title">
                    <Input
                        id="task-title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        required
                        placeholder="e.g. Configure SWIFT message routing"
                        disabled={!canEditAllFields}
                    />
                </FormField>

                <FormGrid>
                    <FormField label="Assignee" htmlFor="task-assignee">
                        <Select
                            id="task-assignee"
                            name="assignee_id"
                            value={formData.assignee_id}
                            onChange={handleChange}
                            disabled={!canEditAllFields}
                        >
                            <option value="">Select assignee</option>
                            {users?.map((u) => (
                                <option key={u.id} value={u.id}>
                                    {u.first_name} {u.last_name}
                                </option>
                            ))}
                        </Select>
                    </FormField>

                    <FormField
                        label="Project"
                        htmlFor="task-project"
                        hint={formData.assignee_id ? 'Limited to projects this assignee belongs to.' : undefined}
                    >
                        <Select
                            id="task-project"
                            name="project_id"
                            value={formData.project_id}
                            onChange={handleChange}
                            disabled={!canEditAllFields}
                        >
                            <option value="">No project</option>
                            {projects?.map((project) => (
                                <option key={project.id} value={project.id}>
                                    {project.project_name}
                                </option>
                            ))}
                        </Select>
                    </FormField>
                </FormGrid>

                <FormField label="Description" htmlFor="task-description">
                    <Textarea
                        id="task-description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={3}
                        placeholder="What needs to be done, and what does done look like?"
                        disabled={!canEditAllFields}
                    />
                </FormField>

                <FormGrid cols={3}>
                    <FormField label="Priority" htmlFor="task-priority">
                        <Select
                            id="task-priority"
                            name="priority"
                            value={formData.priority}
                            onChange={handleChange}
                            disabled={!canEditAllFields}
                        >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="critical">Critical</option>
                        </Select>
                    </FormField>

                    <FormField label="Status" htmlFor="task-status">
                        <Select id="task-status" name="status" value={formData.status} onChange={handleChange}>
                            <option value="todo">To do</option>
                            <option value="in_progress">In progress</option>
                            <option value="blocked">Blocked</option>
                            <option value="done">Done</option>
                            <option value="cancelled">Cancelled</option>
                        </Select>
                    </FormField>

                    <FormField label="Estimated hours" htmlFor="task-hours">
                        <Input
                            id="task-hours"
                            type="number"
                            name="estimated_hours"
                            value={formData.estimated_hours}
                            onChange={handleChange}
                            placeholder="0"
                            step="0.5"
                            min="0"
                            disabled={!canEditAllFields}
                        />
                    </FormField>
                </FormGrid>

                <FormGrid>
                    <FormField label="Start date" htmlFor="task-start">
                        <Input
                            id="task-start"
                            type="date"
                            name="start_date"
                            value={formData.start_date}
                            onChange={handleChange}
                            disabled={!canEditAllFields}
                        />
                    </FormField>

                    <FormField label="Due date" htmlFor="task-due">
                        <Input
                            id="task-due"
                            type="date"
                            name="due_date"
                            value={formData.due_date}
                            onChange={handleChange}
                            disabled={!canEditAllFields}
                        />
                    </FormField>
                </FormGrid>

                <FormField label="Tags" htmlFor="task-tags" hint="Separate with commas.">
                    <Input
                        id="task-tags"
                        name="tags"
                        value={formData.tags}
                        onChange={handleChange}
                        placeholder="backend, api, urgent"
                        disabled={!canEditAllFields}
                    />
                </FormField>
            </form>
        </Modal>
    );
};

export default TaskModal;
